import { memo, useCallback, useEffect, useState } from "react";

import { toast } from "sonner";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button.jsx";
import { Input } from "../../../components/ui/input.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select.jsx";
import { Textarea } from "../../../components/ui/textarea.jsx";
import { useApi } from "../../../context/ApiContext";
import { cn, getCharCount } from "../../../lib/utils.js";
import { formatDateForInput } from "../../../utils/dateUtils";

const TaskForm = ({ newTask, setNewTask, onSubmit, editingTaskId }) => {
  const api = useApi();
  const isDescriptionOverLimit = getCharCount(newTask.description) > 500;

  const [selectedMilestoneDates, setSelectedMilestoneDates] = useState({
    startDate: "",
    endDate: ""
  });

  const handleFieldChange = useCallback(
    (field, value) => {
      setNewTask((prev) => ({ ...prev, [field]: value }));
    },
    [setNewTask]
  );

  // Fetch milestone dates if milestoneId is present but dates are not (e.g. during edit)
  useEffect(() => {
    if (newTask.milestoneId && !newTask.milestoneStartDate) {
      api
        .get(`/milestones/single/${newTask.milestoneId}`)
        .then((res) => {
          setNewTask((prev) => ({
            ...prev,
            milestoneStartDate: res.data.startDate,
            milestoneEndDate: res.data.targetDate || res.data.endDate
          }));
        })
        .catch((err) => console.error("Failed to fetch milestone dates:", err));
    }
  }, [newTask.milestoneId, api, setNewTask, newTask.milestoneStartDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isDescriptionOverLimit) return;

    // Additional validation for milestone dates
    if (newTask.milestoneStartDate && newTask.startDate) {
      const taskStart = new Date(newTask.startDate);
      const milestoneStart = new Date(newTask.milestoneStartDate);
      if (taskStart < milestoneStart) {
        toast.error("Task start date cannot be before milestone start date");
        return;
      }
    }

    if (newTask.milestoneEndDate && newTask.dueDate) {
      const taskDue = new Date(newTask.dueDate);
      const milestoneEnd = new Date(newTask.milestoneEndDate);
      if (taskDue > milestoneEnd) {
        toast.error("Task due date cannot be after milestone end date");
        return;
      }
    }

    onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="projectId" className="text-sm font-medium">
              Project <span className="text-red-500">*</span>
            </Label>
            <LazySearchSelect
              value={newTask.projectId}
              onChange={(val) => handleFieldChange("projectId", val)}
              placeholder="Select Project"
              apiUrl="/projects"
              labelKey="name"
              valueKey="id"
              className="w-full"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="milestoneId" className="text-sm font-medium">
              Milestone <span className="text-red-500">*</span>
            </Label>
            <LazySearchSelect
              key={`milestone-${newTask.projectId}`}
              value={newTask.milestoneId}
              onChange={(val, milestone) => {
                setNewTask((prev) => ({
                  ...prev,
                  milestoneId: val
                }));

                setSelectedMilestoneDates({
                  startDate: milestone?.startDate || "",
                  endDate: milestone?.targetDate || milestone?.endDate || ""
                });
              }}
              placeholder={newTask.projectId ? "Select Milestone" : "Select Project First"}
              apiUrl={newTask.projectId ? `/milestones/${newTask.projectId}` : null}
              labelKey="name"
              valueKey="id"
              className="w-full"
              disabled={!newTask.projectId}
              dataKey="items"
            />
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="title" className="text-sm font-medium">
              Task Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              placeholder="e.g., Foundation work"
              value={newTask.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              className="w-full mobile-text"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="description" className="text-sm font-medium">
            Description <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="description"
            placeholder="Describe the task details..."
            value={newTask.description}
            onChange={(e) => handleFieldChange("description", e.target.value)}
            className={cn(
              "min-h-[100px] w-full mobile-text",
              isDescriptionOverLimit && "border-red-500 focus-visible:ring-red-500"
            )}
            required
            rows={3}
          />
          <WordCount text={newTask.description} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="assignedTo" className="text-sm font-medium">
            Assigned To <span className="text-red-500">*</span>
          </Label>
          <LazySearchSelect
            value={newTask.assignedTo}
            onChange={(val) => handleFieldChange("assignedTo", val)}
            placeholder="Search Users"
            apiUrl="/users"
            dataKey="items"
            labelKey="name"
            valueKey="id"
            className="w-full"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="startDate" className="text-sm font-medium">
              Start Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="startDate"
              type="date"
              value={newTask.startDate}
              onChange={(e) => handleFieldChange("startDate", e.target.value)}
              className="w-full mobile-text"
              required
              min={newTask.milestoneStartDate ? formatDateForInput(selectedMilestoneDates.startDate) : undefined}
              max={newTask.milestoneEndDate ? formatDateForInput(selectedMilestoneDates.endDate) : undefined}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dueDate" className="text-sm font-medium">
              Due Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="dueDate"
              type="date"
              value={newTask.dueDate}
              min={newTask.startDate ? formatDateForInput(newTask.startDate) : undefined}
              max={newTask.milestoneEndDate ? formatDateForInput(selectedMilestoneDates.endDate) : undefined}
              onChange={(e) => handleFieldChange("dueDate", e.target.value)}
              className="w-full mobile-text"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="estimatedHours" className="text-sm font-medium">
              Estimated Hours <span className="text-red-500">*</span>
            </Label>
            <Input
              id="estimatedHours"
              type="number"
              step="0.5"
              value={newTask.estimatedHours}
              onChange={(e) => handleFieldChange("estimatedHours", e.target.value)}
              placeholder="8"
              required
              className="w-full mobile-text"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category" className="text-sm font-medium">
              Category <span className="text-red-500">*</span>
            </Label>
            <Input
              id="category"
              value={newTask.category}
              onChange={(e) => handleFieldChange("category", e.target.value)}
              placeholder="e.g., Electrical"
              required
              className="w-full mobile-text"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="priority" className="text-sm font-medium">
              Priority <span className="text-red-500">*</span>
            </Label>
            <Select value={newTask.priority} onValueChange={(val) => handleFieldChange("priority", val)}>
              <SelectTrigger id="priority" className="w-full mobile-text">
                <SelectValue placeholder="Select Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="status" className="text-sm font-medium">
              Status <span className="text-red-500">*</span>
            </Label>
            <Select value={newTask.status} onValueChange={(val) => handleFieldChange("status", val)}>
              <SelectTrigger id="status" className="w-full mobile-text">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending / To Do</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
        <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg" disabled={isDescriptionOverLimit}>
          {editingTaskId ? "Update Task" : "Create Task"}
        </Button>
      </div>
    </form>
  );
};

export default memo(TaskForm);
